import type { TadaDocumentNode } from 'gql.tada';
import { useState } from 'react';
import { stringifyVariables, useQuery } from 'urql';

type CursorPaginationVariables = Record<string, unknown>;

export default function useCursorPagination<
  TData,
  TVariables extends CursorPaginationVariables,
>({
  query,
  baseVariables,
  getNextPageVariables,
}: {
  query: TadaDocumentNode<TData, TVariables>;
  baseVariables: TVariables;
  getNextPageVariables(data: TData | undefined): TVariables | null;
}) {
  const [pageVariables, setPageVariables] = useState<TVariables | null>(null);
  const [baseResult] = useQuery({
    query,
    variables: baseVariables,
  });
  const [pageResult, reexecutePageQuery] = useQuery({
    query,
    pause: pageVariables === null,
    variables: pageVariables ?? baseVariables,
    requestPolicy: 'network-only',
  });
  const data = pageResult.data ?? baseResult.data;
  const fetching = baseResult.fetching || pageResult.fetching;
  const error = pageVariables ? pageResult.error : baseResult.error;

  const fetchNextPage = () => {
    if (fetching) {
      return;
    }

    const nextPageVariables = getNextPageVariables(data);
    if (!nextPageVariables) {
      return;
    }

    if (
      pageVariables &&
      stringifyVariables(pageVariables) ===
        stringifyVariables(nextPageVariables)
    ) {
      if (pageResult.error) {
        reexecutePageQuery({ requestPolicy: 'network-only' });
      }
      return;
    }

    setPageVariables(nextPageVariables);
  };

  return {
    data,
    error,
    fetching,
    fetchNextPage,
  };
}
